import React from "react";
import { MainLayout } from "./layout";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import withAuth from "./hocs/auth-hocs";
import { queryClient } from "./utils";
import { QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./providers/auth";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CategoryPage from "./pages/ProductManagement/Category/Category";
import { SuspenseWithErrorBoundary } from "./helper/suspense/SuspenseWithErrorBoundary";
import BulletinBoardPage from "./pages/BulletinBoard/BulletinBoard";
import ProductGroupPage from "./pages/ProductManagement/ProductGroup/ProductGroup";
import ProductGroupRegistrationPage from "./pages/ProductManagement/ProductGroup/ProductGroupRegistration/ProductGroupRegistration";
import ProductGroupEditPage from "./pages/ProductManagement/ProductGroup/ProductGroupEdit/ProductGroupEdit";
import ProductGroupDetailPage from "./pages/ProductManagement/ProductGroup/ProductGroupDetail/ProductGroupDetail";
import CodeManagementPage from "./pages/ProductManagement/Code/CodeManagement";
import REFManagementDetailPage from "./pages/ProductManagement/Code/RefManagementDetail";
import QRManagementDetailPage from "./pages/ProductManagement/Code/QRManagementDetail";
import ProductInquiryDetailPage from "./pages/BulletinBoard/Section/ProductInquiry/Detail/ProductInquiryDetail";
import HospitalEditPage from "./pages/MemberManagement/Hospital/Edit/HospitalEdit";
import InquiryDetailPage from "./pages/BulletinBoard/Section/Inquiry1on1/Detail/InquiryDetail";
import InventoryPage from "./pages/ProductManagement/Inventory/Inventory";
import ProductStatistics from "./pages/ProductStatistics/ProductStatistics";
import InventoryDetailPage from "./pages/ProductManagement/Inventory/InventoryDetail/InventoryDetail";
import OrderManagementPage from "./pages/OrderManagement/OrderManagement";
import OrderManagementDetailPage from "./pages/OrderManagement/Detail/OrderManagementDetail";
import TaxInvoicePage from "./pages/TaxInvoices/TaxInvoices";
import DefectReceptionPage from "./pages/DefectReception/DefectReception";
import DefectReceptionDetailPage from "./pages/DefectReception/Detail/DetailDefectReception";
import AdministratorInformationPage from "./pages/AdministratorInformation/AdministratorInformation";
import PushNotificationPage from "./pages/PushNotification/PushNotification";
import PushNotificationDetailPage from "./pages/PushNotification/Detail/PushNotificationDetail";
import NoticeRegisterPage from "./pages/BulletinBoard/Section/Notice/Register/NoticeRegister";
import NoticeDetailPage from "./pages/BulletinBoard/Section/Notice/Detail/NoticeDetail";
import NoticeEditPage from "./pages/BulletinBoard/Section/Notice/Edit/NoticeEdit";
import EventDetailPage from "./pages/BulletinBoard/Section/Event/Detail/EventDetail";
import EventRegisterPage from "./pages/BulletinBoard/Section/Event/Register/EventRegister";
import EventEditPage from "./pages/BulletinBoard/Section/Event/Edit/EventEdit";
import FAQManagementPage from "./pages/BulletinBoard/Section/FAQ/Management/FAQManagement";
import FAQDetailPage from "./pages/BulletinBoard/Section/FAQ/Detail/FAQDetail";
import FAQEditPage from "./pages/BulletinBoard/Section/FAQ/Edit/FAQEdit";
import FAQRegisterPage from "./pages/BulletinBoard/Section/FAQ/Register/FAQRegister";
import VideoRegisterPage from "./pages/BulletinBoard/Section/Video/Register/VideoRegister";
import VideoDetailPage from "./pages/BulletinBoard/Section/Video/Detail/VideoDetail";
import VideoEditPage from "./pages/BulletinBoard/Section/Video/Edit/VideoEdit";
import TermsConditionsPage from "./pages/TermsConditions/TermsConditions";
import BannerManagementPage from "./pages/BannerManagement/BannerManagement";
import BannerRegisterPage from "./pages/BannerManagement/Register/BannerRegister";
import BannerDetailPage from "./pages/BannerManagement/Detail/BannerDetail";
import BannerEditPage from "./pages/BannerManagement/Edit/BannerEdit";
import PatientDetailPage from "./pages/MemberManagement/Patient/PatientDetail";
import Hospital1on1DetailPage from "./pages/MemberManagement/Hospital/1on1/Detail/DetailItem";
import HospitalInquiryDetailPage from "./pages/MemberManagement/Hospital/Inquiry/Detail/DetailItem";
import DashboardStatusPage from "./pages/Dashboard";
import HospitalReviewDetailPage from "./pages/MemberManagement/Hospital/Review/Detail/DetailItem";
import ProductReviewDetailPage from "./pages/BulletinBoard/Section/Review/Detail/ProductReviewDetail";
import HospitalPage from "./pages/MemberManagement/Hospital";
import PatientPage from "./pages/MemberManagement/Patient";
import HospitalDetailItemPage from "./pages/MemberManagement/Hospital/HospitalDetailItem";
import HospitalDetailPointPage from "./pages/MemberManagement/Hospital/Point/HospitalDetailPoint";
import HospitalDetail1on1Page from "./pages/MemberManagement/Hospital/1on1/HospitalDetail1on1";
import { HospitalDetailReviewPage } from "./pages/MemberManagement/Hospital/Review/HospitalDetailReview";
import HospitalDetailInquiryPage from "./pages/MemberManagement/Hospital/Inquiry/HospitalDetailInquiry";
import HospitalDetailOrderPage from "./pages/MemberManagement/Hospital/Order/HospitalDetailOrder";
import HospitalDetailPatientPage from "./pages/MemberManagement/Hospital/Patient/HospitalDetailPatient";
import ExternalServiceTestPage from "./pages/ExternalServiceTest";

// Dashboard
const DashboardStatus = withAuth(DashboardStatusPage);

// Product Management
const Category = withAuth(CategoryPage);
const ProductGroup = withAuth(ProductGroupPage);
const ProductGroupRegistration = withAuth(ProductGroupRegistrationPage);
const ProductGroupDetail = withAuth(ProductGroupDetailPage);
const ProductGroupEdit = withAuth(ProductGroupEditPage);
const HospitalManagement = withAuth(HospitalPage);
const PatientManagement = withAuth(PatientPage);
const CodeManagement = withAuth(CodeManagementPage);
const REFManagementDetail = withAuth(REFManagementDetailPage);
const QRManagementDetail = withAuth(QRManagementDetailPage);
const BulletinBoard = withAuth(BulletinBoardPage);
const ProductInquiryDetail = withAuth(ProductInquiryDetailPage);
const ProductReviewDetail = withAuth(ProductReviewDetailPage);
const InquiryDetail = withAuth(InquiryDetailPage);
const HospitalDetailItem = withAuth(HospitalDetailItemPage);
const HospitalDetailPoint = withAuth(HospitalDetailPointPage);
const HospitalDetail1on1 = withAuth(HospitalDetail1on1Page);
const HospitalDetailReview = withAuth(HospitalDetailReviewPage);
const HospitalDetailInquiry = withAuth(HospitalDetailInquiryPage);
const HospitalDetailOrder = withAuth(HospitalDetailOrderPage);
const HospitalDetailPatient = withAuth(HospitalDetailPatientPage);

const HospitalEdit = withAuth(HospitalEditPage);
const PatientDetail = withAuth(PatientDetailPage);
const Inventory = withAuth(InventoryPage);
const InventoryDetail = withAuth(InventoryDetailPage);
const OrderManagement = withAuth(OrderManagementPage);
const OrderManagementDetail = withAuth(OrderManagementDetailPage);

// Product Statistics
const Statistics = withAuth(ProductStatistics);

const TaxInvoice = withAuth(TaxInvoicePage);
const DefectReception = withAuth(DefectReceptionPage);
const DefectReceptionDetail = withAuth(DefectReceptionDetailPage);

const AdministratorInformation = withAuth(AdministratorInformationPage);
const PushNotification = withAuth(PushNotificationPage);
const PushNotificationDetail = withAuth(PushNotificationDetailPage);
const NoticeRegister = withAuth(NoticeRegisterPage);
const NoticeDetail = withAuth(NoticeDetailPage);
const NoticeEdit = withAuth(NoticeEditPage);
const EventRegister = withAuth(EventRegisterPage);
const EventDetail = withAuth(EventDetailPage);
const EventEdit = withAuth(EventEditPage);
const FAQRegister = withAuth(FAQRegisterPage);
const FAQManagement = withAuth(FAQManagementPage);
const FAQDetail = withAuth(FAQDetailPage);
const FAQEdit = withAuth(FAQEditPage);
const VideoRegister = withAuth(VideoRegisterPage);
const VideoDetail = withAuth(VideoDetailPage);
const VideoEdit = withAuth(VideoEditPage);
const TermsConditions = withAuth(TermsConditionsPage);
const BannerManagement = withAuth(BannerManagementPage);
const BannerRegister = withAuth(BannerRegisterPage);
const BannerDetail = withAuth(BannerDetailPage);
const BannerEdit = withAuth(BannerEditPage);
const Hospital1on1Detail = withAuth(Hospital1on1DetailPage);
const HospitalInquiryDetail = withAuth(HospitalInquiryDetailPage);
const HospitalReviewDetail = withAuth(HospitalReviewDetailPage);

// Test
const ExternalServiceTest = withAuth(ExternalServiceTestPage);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastContainer />
        <Router>
          <MainLayout>
            <SuspenseWithErrorBoundary>
              <Routes>
                <Route path="/" element={<DashboardStatus />} />
                <Route path="/hospital" element={<HospitalManagement />} />
                <Route path="/patient" element={<PatientManagement />} />
                <Route
                  path="/hospital/:id/detail"
                  element={<HospitalDetailItem />}
                />
                <Route
                  path="/hospital/:id/detail/edit"
                  element={<HospitalEdit />}
                />

                <Route
                  path="/hospital/:id/point"
                  element={<HospitalDetailPoint />}
                />

                <Route
                  path="/hospital/:id/1on1"
                  element={<HospitalDetail1on1 />}
                />

                <Route
                  path="/hospital/:id/1on1/:id1on1"
                  element={<Hospital1on1Detail />}
                />

                <Route
                  path="/hospital/:id/inquiry"
                  element={<HospitalDetailInquiry />}
                />

                <Route
                  path="/hospital/:id/inquiry/:idInquiry"
                  element={<HospitalInquiryDetail />}
                />

                <Route
                  path="/hospital/:id/review"
                  element={<HospitalDetailReview />}
                />

                <Route
                  path="/hospital/:id/review/:idReview"
                  element={<HospitalReviewDetail />}
                />

                <Route
                  path="/hospital/:id/patient"
                  element={<HospitalDetailPatient />}
                />

                <Route
                  path="/hospital/:id/order"
                  element={<HospitalDetailOrder />}
                />
                <Route path="/patient/:id" element={<PatientDetail />} />
                <Route
                  path="/product-management/product-group"
                  element={<ProductGroup />}
                />
                <Route
                  path="/product-management/registration"
                  element={<ProductGroupRegistration />}
                />
                <Route
                  path="/product-management/product-group/:id"
                  element={<ProductGroupDetail />}
                />
                <Route
                  path="/product-management/product-group/:id/edit"
                  element={<ProductGroupEdit />}
                />
                <Route
                  path="/product-management/category"
                  element={<Category />}
                />
                <Route
                  path="/product-management/code"
                  element={<CodeManagement />}
                />
                <Route
                  path="/product-management/code/:id"
                  element={<REFManagementDetail />}
                />
                <Route
                  path="/product-management/code/:id/:prodId"
                  element={<QRManagementDetail />}
                />
                <Route
                  path="/product-management/inventory"
                  element={<Inventory />}
                />
                <Route
                  path="/product-management/inventory/:id"
                  element={<InventoryDetail />}
                />
                <Route path="/bulletin-board" element={<BulletinBoard />} />
                <Route
                  path="/bulletin-board/product-inquiry/:id"
                  element={<ProductInquiryDetail />}
                />
                <Route
                  path="/bulletin-board/review/:id"
                  element={<ProductReviewDetail />}
                />
                <Route
                  path="/bulletin-board/inquiry/:id"
                  element={<InquiryDetail />}
                />
                <Route
                  path="/bulletin-board/notice/register"
                  element={<NoticeRegister />}
                />
                <Route
                  path="/bulletin-board/notice/:id"
                  element={<NoticeDetail />}
                />
                <Route
                  path="/bulletin-board/notice/:id/edit"
                  element={<NoticeEdit />}
                />

                <Route
                  path="/bulletin-board/event/register"
                  element={<EventRegister />}
                />
                <Route
                  path="/bulletin-board/event/:id"
                  element={<EventDetail />}
                />
                <Route
                  path="/bulletin-board/event/:id/edit"
                  element={<EventEdit />}
                />
                <Route
                  path="/bulletin-board/faq/register"
                  element={<FAQRegister />}
                />
                <Route
                  path="/bulletin-board/faq/management"
                  element={<FAQManagement />}
                />

                <Route path="/bulletin-board/faq/:id" element={<FAQDetail />} />
                <Route
                  path="/bulletin-board/faq/:id/edit"
                  element={<FAQEdit />}
                />
                <Route
                  path="/bulletin-board/video/register"
                  element={<VideoRegister />}
                />
                <Route
                  path="/bulletin-board/video/:id"
                  element={<VideoDetail />}
                />
                <Route
                  path="/bulletin-board/video/:id/edit"
                  element={<VideoEdit />}
                />
                <Route
                  path="/banner-management"
                  element={<BannerManagement />}
                />
                <Route
                  path="/banner-management/register"
                  element={<BannerRegister />}
                />

                <Route
                  path="/banner-management/:id"
                  element={<BannerDetail />}
                />

                <Route
                  path="/banner-management/:id/edit"
                  element={<BannerEdit />}
                />
                <Route path="/statistic" element={<Statistics />} />
                <Route
                  path="/terms-and-condition"
                  element={<TermsConditions />}
                />
                <Route path="/order-management" element={<OrderManagement />} />
                <Route
                  path="/order-management/:id"
                  element={<OrderManagementDetail />}
                />
                <Route path="/tax-invoices" element={<TaxInvoice />} />
                <Route path="/defect-reception" element={<DefectReception />} />
                <Route
                  path="/defect-reception/detail"
                  element={<DefectReceptionDetail />}
                />
                <Route
                  path="/administrator-information"
                  element={<AdministratorInformation />}
                />
                <Route
                  path="/push-notification"
                  element={<PushNotification />}
                />
                <Route
                  path="/push-notification/:id"
                  element={<PushNotificationDetail />}
                />
                <Route path="/dashboard-status" element={<DashboardStatus />} />
                <Route
                  path="/external-service-test"
                  element={<ExternalServiceTest />}
                />
              </Routes>
            </SuspenseWithErrorBoundary>
          </MainLayout>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
